import React, { useState, useEffect, useRef } from 'react';
import {
  Engine as EngineCore,
  Scene as SceneCore,
  Camera,
  Nullable,
  DefaultRenderingPipeline,
  DepthOfFieldEffectBlurLevel,
  Color3,
  Color4,
  Vector3,
  DefaultLoadingScreen,
  Mesh,
  ArcRotateCamera,
  HemisphericLight,
  DirectionalLight,
  PointLight,
  HighlightLayer,
  ShadowGenerator,
} from '@babylonjs/core';

import './App.css';
import Repere from './games/core/Repere';
import Routing from './routing/Routing';
import CartaBackground from './games/core/CartaBackground';
import ShowReelManager from './manager/ShowReelManager';
import DataManager from './manager/DataManager';
import FPS from './games/core/FPS';
import { LoadingScreenManager } from './manager/LoadingScreenManager';
import LoadingScreen from './componement/LoadingScreen';
import { SoundManager } from './manager/SoundManager';
import { SceneManager } from './manager/SceneManager';

import i18next from 'i18next';

import { useUserUpdate } from './context/UserProvider';
import { InfosUser } from './data/InfosUser';
import { useRondaModal } from './context/ModalProvider';
import useRondaFetch from './hook/useRondaFetch';
import { useRondaLoader } from './context/LoaderProvider';
import { log, onMessageListener } from './manager/FirebaseManager';
import { useRondaMessenger } from './context/MessengerProvider';
import InfosProfil from './data/InfosProfil';
import { useMainScreenPrompt } from './context/PromptProvider';
import useProperty from './hook/useProperty';

const App: React.FC = () => {

  const { prompt, promptToInstall } = useMainScreenPrompt()!;
  const { updateProperty } = useProperty()!;
  const { handleModal } = useRondaModal()!;
  const { handleMessage } = useRondaMessenger()!;
  const { get, response } = useRondaFetch(undefined, undefined, undefined, "Full");
  const setUserInfos = useUserUpdate()!;
  const { handleLoader } = useRondaLoader()!;
  const countFetch = useRondaFetch();

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<EngineCore | null>(null);

  const [scene, setScene] = useState<Nullable<SceneCore>>(null);
  const [pipeline, setPipeline] = useState<DefaultRenderingPipeline>();
  const [srm, setSRM] = useState<ShowReelManager>();
  const [isLoading, setIsLoading] = useState(true);
  const [preloadedImages] = useState<Array<HTMLImageElement>>([]);

  const showRepere: boolean = false;

  // ── Install prompt ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (prompt) {
      showInstallPromotion();
      updateProperty("WEB");
    }
  }, [prompt]);

  const showInstallPromotion = () => {
    handleMessage({
      title: "Install the app ?",
      message: "You can install this game on your device",
      type: "installation",
      callback: () => {
        promptToInstall();
        log("install_app", { version: "v1" });
        updateProperty("APP");
      }
    });
  };

  // ── Unlock audio ───────────────────────────────────────────────────────────
  useEffect(() => {
    const unlocksoud = () => {
      if (!EngineCore.audioEngine?.unlocked)
        EngineCore.audioEngine?.unlock();
      document.onclick = null;
    };
    document.onclick = unlocksoud;
  }, []);

  // ── Firebase messaging ─────────────────────────────────────────────────────
  useEffect(() => {
    onMessageListener().then(payload => {
      const payloadData = (payload as any);
      let link = payloadData.data?.link;
      if (link) link = link.replace(DataManager.ABSOLUT_PATH, '');

      const from = payloadData.data?.sender
        ? new InfosProfil(JSON.parse(payloadData.data.sender))
        : new InfosProfil();

      handleMessage({
        title: payloadData.notification.title,
        message: payloadData.notification.body,
        from,
        type: payloadData.data?.type ?? "notification",
        link
      });
    }).catch(err => console.log('FCM failed:', err));
  }, []);

  // ── Helpers ────────────────────────────────────────────────────────────────
  function configureData() {
    const idInfosLanguage = DataManager.DATA_LANGUAGES.find(
      i => i.locale === i18next.language
    )?.id;

    setUserInfos((prevIU: InfosUser) => ({
      ...prevIU,
      dataGames: DataManager.DATA_GAMES,
      infosProfil: {
        ...prevIU.infosProfil,
        infosSettings: {
          ...prevIU.infosProfil.infosSettings,
          idInfosLanguage
        }
      }
    }));

    DataManager.DATA_PRELOAD_IMAGES.forEach((fileName: any, index: number) => {
      DataManager.DATA_PRELOAD_IMAGES[index] = DataManager.ABSOLUT_PATH + fileName;
      const img = new Image();
      img.src = DataManager.DATA_PRELOAD_IMAGES[index];
      preloadedImages.push(img);
    });
  }

  const updateAdCount = (element: any) => {
    if (element.video)
      countFetch.put('api/ad-videos/' + element.id, { data: { count: element.count + 1 } });
    else
      countFetch.put('api/ad-versos/' + element.id, { data: { count: element.count + 1 } });
  };

  // ── Pipeline DOF ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (pipeline?.isSupported) {
      pipeline.depthOfFieldBlurLevel = DepthOfFieldEffectBlurLevel.Medium;
      pipeline.depthOfField.focalLength = 0;
    }
  }, [pipeline]);

  // ── Initialisation BabylonJS ───────────────────────────────────────────────
  useEffect(() => {
    if (!canvasRef.current) return;

    const loadingScreen = new LoadingScreenManager(" ");
    DefaultLoadingScreen.prototype.displayLoadingUI = loadingScreen.displayLoadingUI;

    const engine = new EngineCore(canvasRef.current, true, {
      adaptToDeviceRatio: true,
    });
    engineRef.current = engine;
    engine.disableUniformBuffers = true;

    const bScene = new SceneCore(engine);
    bScene.hoverCursor = "pointer";
    bScene.ambientColor = Color3.White();
    bScene.clearColor = new Color4(64 / 255, 102 / 255, 35 / 255, 1);
    bScene.autoClear = false;
    bScene.autoClearDepthAndStencil = false;

    // ── Caméra ────────────────────────────────────────────────────────────────
    const camera = new ArcRotateCamera(
      "Camera", -Math.PI / 2, Math.PI / 8, 55, Vector3.Up(), bScene
    );
    camera.fov = 0.8;
    camera.fovMode = Camera.FOVMODE_HORIZONTAL_FIXED;
    camera.minZ = 1;
    camera.inputs.clear();

    // ── Lumières ──────────────────────────────────────────────────────────────
    // 1. Lumière ambiante hémisphérique
    const ambLight = new HemisphericLight("Ambilight", Vector3.Up(), bScene);
    ambLight.intensity = 0.3;
    ambLight.diffuse = new Color3(1, 0.96, 0.96);
    ambLight.specular = new Color3(0, 0, 0); // pas de reflet spéculaire

    // 2. Lumière directionnelle — SANS ombres (shadowEnabled = false dans l'original)
    // ⚠️ Vector3.Zero() comme direction est invalide pour DirectionalLight
    // On utilise setDirectionToTarget pour calculer la direction depuis sa position
    const dirLight = new DirectionalLight(
      "ShadowLight",
      new Vector3(0, -1, 0), // direction initiale valide (vers le bas)
      bScene
    );
    dirLight.position = new Vector3(0, 60, 10);
    dirLight.setDirectionToTarget(Vector3.Zero()); // recalcule vers l'origine
    dirLight.specular = new Color3(0, 0, 0);
    dirLight.intensity = 0.6;
    dirLight.shadowMinZ = 1;
    dirLight.shadowMaxZ = 2500;

    // NE PAS mettre shadowEnabled = false ici

    // Réactiver le ShadowGenerator — nécessaire pour SceneManager.buildCards()
    const shadowGen = new ShadowGenerator(256, dirLight);
    shadowGen.useBlurExponentialShadowMap = true;
    shadowGen.blurKernel = 16;
    shadowGen.darkness = 0.3;
    shadowGen.forceBackFacesOnly = true;
    shadowGen.depthScale = 10;

    // 3. Point light d'accentuation
    const pointLight = new PointLight("Spotlight", new Vector3(0, 80, 10), bScene);
    pointLight.specular = new Color3(1.2, 1.2, 1.2);
    pointLight.intensity = 0.3;

    // ── Highlight layer ───────────────────────────────────────────────────────
    const highlightLayer = new HighlightLayer("h1", bScene);
    highlightLayer.isEnabled = true;

    // ── Pipeline de rendu ─────────────────────────────────────────────────────
    // false = HDR désactivé — évite le noircissement lié au tone mapping
    const pipe = new DefaultRenderingPipeline('default', false, bScene, [camera]);
    setPipeline(pipe);

    // ── Démarrage scène ───────────────────────────────────────────────────────
    engine.displayLoadingUI();

    bScene.executeOnceBeforeRender(() => {
      engine.hideLoadingUI();
      console.log("✅ executeOnceBeforeRender déclenché");

      const cartaScene = new SceneManager(bScene, onReady, handleLoader, updateAdCount);
      SceneManager.self = cartaScene;
      console.log("✅ SceneManager créé, en attente de onReady...");

      function onReady(cardsList: Array<Mesh>) {
        console.log("✅ onReady appelé, cards :", cardsList.length);
        setSRM(new ShowReelManager(cardsList));
        DataManager.CARDS_LIST = cardsList;
        handleLoader(true, "full sound");
        SoundManager.init(bScene);
        loadNextSound(0);
      }

      function loadNextSound(index: number) {
        if (index < DataManager.DATA_SOUNDS.length) {
          const nextSound = DataManager.DATA_SOUNDS[index];
          SoundManager.add(nextSound.name, nextSound.link, nextSound.group, nextSound.type, () => {
            loadNextSound(index + 1);
          });
        } else {
          loadedSound();
        }
      }

      function loadedSound() {
        if (engine.getFps() < 30) DataManager.RESOLUTION = "sd";
        handleLoader(false);
        configureData();
        setScene(bScene);
        setIsLoading(false);
        SoundManager.updateVolume(0.5, 0.5);
        SoundManager.ready = true;
      }
    });

    engine.runRenderLoop(() => bScene.render());

    const handleResize = () => engine.resize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      highlightLayer.dispose();
      bScene.dispose();
      engine.dispose();
    };
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden' }}>

      {/* Canvas BabylonJS — couche de base */}
      <canvas
        ref={canvasRef}
        id="sample-canvas"
        style={{
          position: 'absolute',
          top: 0, left: 0,
          width: '100%', height: '100%',
          zIndex: 0,
        }}
      />

      {/* Composants 3D — return null, pas de DOM */}
      {scene && <CartaBackground scene={scene} />}
      {scene && <FPS scene={scene} visibility={true} />}
      {scene && <Repere scene={scene} visibility={showRepere} />}

      {/* UI React — par dessus le canvas */}
      {scene && (
        <div style={{
          position: 'absolute',
          top: 0, left: 0,
          width: '100%', height: '100%',
          zIndex: 1,
          pointerEvents: 'none',
        }}>
          <Routing scene={scene} srm={srm} pipeline={pipeline} />
        </div>
      )}

      {/* Loading screen — au dessus de tout */}
      {isLoading && (
        <div style={{
          position: 'absolute',
          inset: 0,
          zIndex: 100,
          pointerEvents: 'all',
        }}>
          <LoadingScreen />
        </div>
      )}

    </div>
  );
};

export default App;